import React from 'react'
  import { MDXTag } from '@mdx-js/tag'


  const layoutProps = {};
export default class MDXContent extends React.Component {
  constructor(props) {
    super(props);
    this.layout = null;
  }

  render() {
    const {
      components,
      ...props
    } = this.props;
    return <MDXTag name="wrapper" components={components}><MDXTag name="h1" components={components}>{`Props`}</MDXTag>
      <MDXTag name="h2" components={components}>{`Styled System`}</MDXTag>
      <MDXTag name="p" components={components}>{`Rebass components use `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://github.com/jxnblk/styled-system"
        }}>{`styled-system`}</MDXTag>{` for responsive, theme-based style props.
Each Rebass component extends the base `}<MDXTag name="inlineCode" components={components} parentName="p">{`Box`}</MDXTag>{` component, which includes several general-purpose style props.`}</MDXTag>
      <MDXTag name="h2" components={components}>{`Colors`}</MDXTag>
      <MDXTag name="p" components={components}>{`All Rebass components use `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://github.com/jxnblk/styled-system"
        }}>{`styled-system`}</MDXTag>{`'s `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://github.com/jxnblk/styled-system/blob/master/docs/api.md#color-responsive"
        }}>{`color`}</MDXTag>{` function to add the `}<MDXTag name="inlineCode" components={components} parentName="p">{`color`}</MDXTag>{` and `}<MDXTag name="inlineCode" components={components} parentName="p">{`bg`}</MDXTag>{` props.
The `}<MDXTag name="inlineCode" components={components} parentName="p">{`color`}</MDXTag>{` and `}<MDXTag name="inlineCode" components={components} parentName="p">{`bg`}</MDXTag>{` props make using colors from the color palette simple to help promote design consistency.`}</MDXTag>
      <MDXTag name="p" components={components}>{`The color values can be defined in the `}<MDXTag name="inlineCode" components={components} parentName="p">{`theme.colors`}</MDXTag>{` object.`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-.jsx"
        }}>{`<Box color='white' bg='magenta' p={3}>
  Hello
</Box>
`}</MDXTag></MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`// Keys reference values in the color palette object
<Text color='blue' />

// Background color can be set with the \`bg\` prop
<Button bg='red' />

// Values that do not map to a key in \`theme.colors\` can be used
<Button bg='tomato' />

// Arrays can be used to change colors responsively
<Text color={[ 'blue', 'green' ]} />
`}</MDXTag></MDXTag>
      <MDXTag name="h2" components={components}>{`Margin and Padding`}</MDXTag>
      <MDXTag name="p" components={components}>{`All Rebass component use the `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://github.com/jxnblk/styled-system/blob/master/docs/api.md#space-responsive"
        }}>{`space`}</MDXTag>{` utility from `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://github.com/jxnblk/styled-system"
        }}>{`styled-system`}</MDXTag>{` to handle responsive margin and padding props based on a global spacing scale (`}<MDXTag name="inlineCode" components={components} parentName="p">{`theme.space`}</MDXTag>{`).
The margin and padding props help promote consistency in layout
without the need to add custom margin and padding declarations throughout an application.
The margin and padding props use a shorthand syntax, similar to other OOCSS approaches and many CSS libraries.`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-.jsx"
        }}>{`<Box
  p={3}
  mx={2}
  my={4}
  color='white'
  bg='blue'>
  Hello
</Box>
`}</MDXTag></MDXTag>
      <MDXTag name="ul" components={components}>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`m`}</MDXTag>{`: margin`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`mt`}</MDXTag>{`: margin-top`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`mr`}</MDXTag>{`: margin-right`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`mb`}</MDXTag>{`: margin-bottom`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`ml`}</MDXTag>{`: margin-left`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`mx`}</MDXTag>{`: margin-left and margin-right`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`my`}</MDXTag>{`: margin-top and margin-bottom`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`p`}</MDXTag>{` : padding`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`pt`}</MDXTag>{`: padding-top`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`pr`}</MDXTag>{`: padding-right`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`pb`}</MDXTag>{`: padding-bottom`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`pl`}</MDXTag>{`: padding-left`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`px`}</MDXTag>{`: padding-left and padding-right`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="inlineCode" components={components} parentName="li">{`py`}</MDXTag>{`: padding-top and padding-bottom`}</MDXTag>
      </MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`// Numbers reference steps on the spacing scale
// e.g. 8px
<Text m={2} />

// Numbers greater than the length of \`theme.space.length\` are converted to pixels
<Text my={256} />

// Negative values can be used to add negative margins
<Text mx={-2} />

// Strings can be used for other values
<Text mx='auto' />

// Arrays can be used for mobile-first responsive styles
<Text m={[ 0, 1, 2 ]} />
`}</MDXTag></MDXTag>
      <MDXTag name="h2" components={components}>{`Font Size`}</MDXTag>
      <MDXTag name="p" components={components}>{`The `}<MDXTag name="inlineCode" components={components} parentName="p">{`fontSize`}</MDXTag>{` prop can pick up values from a typographic scale defined in your theme as a `}<MDXTag name="inlineCode" components={components} parentName="p">{`theme.fontSizes`}</MDXTag>{` array.`}</MDXTag>
      <MDXTag name="h2" components={components}>{`Width`}</MDXTag>
      <MDXTag name="p" components={components}>{`The `}<MDXTag name="inlineCode" components={components} parentName="p">{`width`}</MDXTag>{` prop can set fixed or percentage-based widths on an element.`}</MDXTag>
      <MDXTag name="p" components={components}>{`The `}<MDXTag name="inlineCode" components={components} parentName="p">{`width`}</MDXTag>{` prop accepts number, string, or array values, where:`}</MDXTag>
      <MDXTag name="ul" components={components}>
        <MDXTag name="li" components={components} parentName="ul">{`Numbers between 0 and 1 are converted to percentage based widths (e.g. `}<MDXTag name="inlineCode" components={components} parentName="li">{`1/2`}</MDXTag>{` becomes `}<MDXTag name="inlineCode" components={components} parentName="li">{`50%`}</MDXTag>{`)`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul">{`Numbers greater than 1 are converted to pixels`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul">{`Strings can be used for other CSS values (e.g. `}<MDXTag name="inlineCode" components={components} parentName="li">{`50vw`}</MDXTag>{` or `}<MDXTag name="inlineCode" components={components} parentName="li">{`30em`}</MDXTag>{`)`}</MDXTag>
        <MDXTag name="li" components={components} parentName="ul">{`Arrays can be used for responsive styles`}</MDXTag>
      </MDXTag>
      <MDXTag name="h2" components={components}>{`Responsive Styles`}</MDXTag>
      <MDXTag name="p" components={components}>{`All Rebass props accept arrays as values to set mobile-first responsive styles.
The first value is not scoped to a media query and applies to all breakpoints.
Each value after the first corresponds to a media query derived from `}<MDXTag name="inlineCode" components={components} parentName="p">{`theme.breakpoints`}</MDXTag>{`.`}</MDXTag>
      <MDXTag name="p" components={components}>{`See the `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://github.com/jxnblk/styled-system/blob/master/docs/responsive-styles.md"
        }}>{`styled-system`}</MDXTag>{` docs for more info.`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-.jsx"
        }}>{`<Flex flexWrap='wrap'>
  <Box
    width={[ 1, 1/2 ]}
    p={2}
    color='white'
    bg='blue'>
    Hello
  </Box>
  <Box
    width={[ 1, 1/2 ]}
    p={2}
    color='white'
    bg='dark'>
    Hello
  </Box>
</Flex>
`}</MDXTag></MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`<Text
  width={[
    1,    // 100% width at the smallest breakpoint
    1/2,  // 50% width at the next breakpoint
    null, // null skips a breakpoint
    1/4   // 25% width at the next
  ]}
/>
`}</MDXTag></MDXTag>
      <MDXTag name="h2" components={components}><MDXTag name="inlineCode" components={components} parentName="h2">{`css`}</MDXTag>{` prop`}</MDXTag>
      <MDXTag name="p" components={components}>{`Previous versions of Rebass included an implementation for handling a `}<MDXTag name="inlineCode" components={components} parentName="p">{`css`}</MDXTag>{` prop.
To enable this prop with styled-components v4, add the `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://www.styled-components.com/docs/api#css-prop"
        }}><MDXTag name="inlineCode" components={components} parentName="a">{`babel-plugin-styled-components`}</MDXTag></MDXTag>{` plugin to your Babel configuration.`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`// example of using the \`css\` prop
const GrowingButton = props =>
  <Button
    {...props}
    css={{
      transition: 'transform .1s ease-out',
      '&:hover': {
        transform: 'scale(1.1)'
      }
    }}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h2" components={components}>{`Component-Specific Props`}</MDXTag>
      <MDXTag name="p" components={components}>{`Refer to each component's docs for addition props:`}</MDXTag>
      <MDXTag name="ul" components={components}>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="a" components={components} parentName="li" props={{
            "href": "/Box"
          }}>{`Box`}</MDXTag></MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="a" components={components} parentName="li" props={{
            "href": "/Flex"
          }}>{`Flex`}</MDXTag></MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="a" components={components} parentName="li" props={{
            "href": "/Text"
          }}>{`Text`}</MDXTag></MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="a" components={components} parentName="li" props={{
            "href": "/Heading"
          }}>{`Heading`}</MDXTag></MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="a" components={components} parentName="li" props={{
            "href": "/Button"
          }}>{`Button`}</MDXTag></MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="a" components={components} parentName="li" props={{
            "href": "/Link"
          }}>{`Link`}</MDXTag></MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="a" components={components} parentName="li" props={{
            "href": "/Image"
          }}>{`Image`}</MDXTag></MDXTag>
        <MDXTag name="li" components={components} parentName="ul"><MDXTag name="a" components={components} parentName="li" props={{
            "href": "/Card"
          }}>{`Card`}</MDXTag></MDXTag>
      </MDXTag>
           </MDXTag>;
  }

}
MDXContent.isMDXComponent = true;
export const _frontmatter = {};
      